import { ModalAction, ModalTitle } from '@overlayqa/shared/ui';
import { useState, useMemo, useRef } from 'react';
import { useModalA11y } from '@/hooks/useModalA11y';
import { BrowserIcon } from '@hugeicons/core-free-icons';
import { ACTIONABLE_TOAST_MIN_MS, CWS_INSTALL_URL, FIGMA_DESIGN_LINK_REVIEW_ERROR, URL_OWN_APP_REVIEW_ERROR, isFigmaDesignArtifactUrl } from '@overlayqa/shared';
import { useApiMutation } from '@/hooks/useApiMutation';
import { useExtensionDetection } from '@/hooks/useExtensionDetection';
import { clientReviewsApi } from '@/lib/api/clientReviews';
import { log } from '@/lib/logger';
import { openCreatedReview } from '@/lib/openCreatedReview';
import { buildCreatedReviewUrl } from '@/lib/createdReviewUrl';
import { classifyUnreachableUrl, type UnreachableReason } from '@/lib/captureIssuesUrl';
import { ExtensionCaptureHint } from './ExtensionCaptureHint';
import { InteractiveReview } from './InteractiveReview';
import { ModalPortal } from '@/components/shared/ModalPortal';
import { useAuth } from '@/lib/auth';
import { endLiveReview } from '@/lib/api/liveReviewStream';
import { toast } from 'sonner';

interface LiveReviewModalProps {
  projectId: string;
  onClose: () => void;
  /** Optional URL to pre-fill the form with (e.g., from the project's scanUrl or most recent issue). */
  initialUrl?: string;
  /**
   * Workflow panel the created review should open on (`&workflow=<id>` on the
   * review URL; the server allowlists the value, see parseOpenWorkflow). Set by
   * the Schedule page's "Review findings" action so the owner lands on the
   * accessibility audit instead of three clicks away from it. Absent for every
   * other caller, whose URLs are unchanged.
   */
  openWorkflow?: string;
  /**
   * An error to show under the field on mount. Set by useCaptureIssues when
   * the direct Capture Issues create failed: the modal opens pre-filled with
   * the URL that failed and says why, inline, so the user can edit and retry
   * instead of reading a toast that has already faded.
   */
  initialError?: string;
}

export function LiveReviewModal({ projectId, onClose, initialUrl, openWorkflow, initialError }: LiveReviewModalProps) {
  const [pageUrl, setPageUrl] = useState(initialUrl ?? '');
  const { getToken } = useAuth();
  const [urlError, setUrlError] = useState<string | null>(initialError ?? null);
  // Set when the server provisions a Steel remote-browser session (streamed
  // mode). Holds the liveViewUrl + reviewId so InteractiveReview can open the
  // SSE stream. When set, the modal is replaced by a full-screen overlay.
  const [streamedSession, setStreamedSession] = useState<{ liveViewUrl: string; reviewId: string; pageUrl: string } | null>(null);
  const { detected: extensionDetected, checking: extensionChecking } = useExtensionDetection();
  const dialogRef = useRef<HTMLDivElement>(null);
  // Inactive on the streamed path below, where this modal is replaced wholesale
  // by the full-screen InteractiveReview overlay: Escape there would tear down
  // a live review session rather than dismiss a dialog.
  useModalA11y(!streamedSession, dialogRef, () => handleUserClose());

  // True once the user dismissed the modal (X, backdrop, or Cancel) while a
  // create was still in flight. The create keeps running server-side and the
  // hook-level onSuccess still fires after unmount, so without this flag a
  // 60-100s create would window.open a surprise tab (or get popup-blocked
  // silently) long after the user walked away. See the 2026-08-03 dead-click
  // incident: openCreatedReview.ts has the full story.
  const closedByUserRef = useRef(false);
  const handleUserClose = () => {
    closedByUserRef.current = true;
    onClose();
  };

  // Pre-flight: detect localhost / private IPs / non-http protocols the server
  // cannot reach so we can steer the user to the extension before they hit a
  // failed proxy. ONE source with the direct Capture Issues path
  // (useCaptureIssues), which refuses to create at such a URL and opens this
  // modal instead: lib/captureIssuesUrl.ts.
  const localKind = useMemo<UnreachableReason | null>(
    () => classifyUnreachableUrl(pageUrl),
    [pageUrl],
  );

  // Pre-flight: a figma.com design link is a document in Figma's editor, not
  // a page the proxy can render — the review canvas stays permanently blank
  // (incident 2026-08-12). Catch it at the field, before the server 400 would.
  const figmaDesignLink = useMemo(() => {
    const trimmed = pageUrl.trim();
    if (!trimmed) return false;
    return isFigmaDesignArtifactUrl(trimmed.startsWith('http') ? trimmed : `https://${trimmed}`);
  }, [pageUrl]);

  log.info('[LiveReviewModal] Opened', { projectId, extensionDetected, extensionChecking });

  const { mutate, isPending } = useApiMutation<
    Awaited<ReturnType<typeof clientReviewsApi.createLiveReview>>,
    { projectId: string; pageUrl: string }
  >({
    mutationFn: (token, data) => clientReviewsApi.createLiveReview(token, data),
    onSuccess: (data) => {
      const url = data?.shareUrl ?? null;
      log.info('[LiveReviewModal] Review created', {
        shareUrl: url,
        modeSwitched: data?.modeSwitched,
        liveAvailable: data?.liveAvailable,
        mode: data?.mode,
      });
      // Build the review URL through the SHARED builder. It was inline here
      // until 2026-08-22, when the Schedule page's Capture Issues action
      // became a second caller that creates a review with no modal: two
      // hand-built query strings would drift the moment either gained a param.
      // Why each param matters (and why order is pinned) lives in that module.
      const fullUrl = buildCreatedReviewUrl({
        shareUrl: url,
        liveAvailable: data?.liveAvailable,
        extensionDetected,
        authHandoff: data?.authHandoff,
        openWorkflow,
      });
      // The user closed the modal while the create was in flight: never open
      // a surprise tab or hijack their navigation. openCreatedReview shows a
      // "ready" toast with an Open action instead (works after unmount).
      if (closedByUserRef.current) {
        if (data?.responseMode === 'streamed' && data.id) {
          const close = async () => {
            try { await endLiveReview((await getToken()) ?? undefined, projectId, data.id); }
            catch { toast.error('The remote review could not close.', { duration: ACTIONABLE_TOAST_MIN_MS, action: { label: 'Retry', onClick: () => { void close(); } } }); }
          };
          void close();
          return;
        }
        if (fullUrl) openCreatedReview(fullUrl, true);
        return;
      }
      // Streamed path: server provisioned a Steel remote-browser session.
      // Render InteractiveReview as a full-screen overlay so the OverlayQA
      // pin layer, SSE stream, and click→issue flow are all active. The modal
      // form is hidden; the surface stays until the user explicitly closes it.
      if (data?.responseMode === 'streamed' && data?.liveViewUrl && data?.id) {
        setStreamedSession({ liveViewUrl: data.liveViewUrl, reviewId: data.id, pageUrl: data.pageUrl || (pageUrl.trim().startsWith('http') ? pageUrl.trim() : `https://${pageUrl.trim()}`) });
        return;
      }
      if (fullUrl) openCreatedReview(fullUrl, false);
      onClose();
    },
    onError: (error) => {
      setUrlError(error.message || 'Failed to create review');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setUrlError(null);

    if (localKind || figmaDesignLink) return;

    const trimmed = pageUrl.trim();
    if (!trimmed) {
      setUrlError('URL is required');
      return;
    }

    // Auto-prepend https:// if missing
    const fullUrl = trimmed.startsWith('http') ? trimmed : `https://${trimmed}`;

    try {
      new URL(fullUrl);
    } catch {
      setUrlError('Enter a valid URL');
      return;
    }

    mutate({ projectId, pageUrl: fullUrl });
  };

  // Show the install callout only when we've confirmed the extension is NOT
  // present. Suppress while still checking to avoid a flash on first paint.
  const showInstallCallout = !extensionChecking && !extensionDetected;

  // The URL is pre-filled from the project's scanUrl / most recent issue. Tell
  // the user it was reused (so a mis-filled URL is obvious and clearly editable)
  // while the field is still the untouched pre-fill. A local pre-fill shows the
  // ExtensionCaptureHint instead, so suppress this there.
  const showReusedHint = !!initialUrl && pageUrl === initialUrl && !localKind && !figmaDesignLink;

  // Streamed path: replace the entire modal with a full-screen InteractiveReview
  // overlay. Fixed inset-0 at z-60 (above the modal's z-50) so it covers everything.
  // The OverlayQA pin layer, SSE stream, and click→issue flow are all active here.
  // LIVE-VERIFY: confirm Steel iframe renders + SSE stream opens + pins appear on
  // real hover events once Steel sessions are provisioned in the deployed environment.
  if (streamedSession) {
    const overlay1 = (
      <div className="fixed inset-0 z-[60] bg-gray-950">
        <InteractiveReview
          liveViewUrl={streamedSession.liveViewUrl}
          pageUrl={streamedSession.pageUrl}
          reviewId={streamedSession.reviewId}
          projectId={projectId}
          status="live"
          remoteViewport={{ width: 1280, height: 800 }}
          onClose={() => {
            setStreamedSession(null);
            onClose();
          }}
        />
      </div>
    );

    return <ModalPortal>{overlay1}</ModalPortal>;
  }

  const overlay2 = (
    <div
      ref={dialogRef}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="live-review-modal-title"
      onClick={(e) => { if (e.target === e.currentTarget) handleUserClose(); }}
    >
      <div className="bg-white rounded-xl w-full max-w-md shadow-xl overflow-hidden">
        {/* Header */}
        <div className="px-6 py-2 border-b border-gray-100 flex items-center justify-between">
          <ModalTitle icon={BrowserIcon} id="live-review-modal-title" className="oq-dialog__title">Capture issues</ModalTitle>
          <button
            type="button"
            onClick={handleUserClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2"
            aria-label="Close"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="px-6 pt-4 pb-5">
          {/* Teaches the feature name AFTER the outcome label hooked the user:
              "Team Review" is the internal/marketed name and nothing in-product
              defines it before this point (the review page itself renders the
              project name, never the feature name — review-page/markup.ts:39).
              Docs, marketing and the help corpus all speak "team review", so
              this line is what keeps that vocabulary connected. */}
          <p className="modal-intro mb-4">
            Start a team review: you and your teammates click elements on any page to file issues. No extension needed.
          </p>
          <label htmlFor="live-review-url" className="field-label">
            Page URL
          </label>
          <input
            id="live-review-url"
            type="text"
            className={`input input-lg w-full ${urlError ? 'input-error' : ''}`}
            placeholder="https://acme-corp.com/pricing"
            value={pageUrl}
            onChange={(e) => { setPageUrl(e.target.value); setUrlError(null); }}
            autoFocus
            disabled={isPending}
            aria-invalid={!!urlError || figmaDesignLink || localKind === 'own-app'}
            aria-describedby={
              [
                urlError ? 'live-review-url-error' : null,
                figmaDesignLink ? 'live-review-figma-link-error' : null,
                localKind === 'own-app' ? 'live-review-own-app-error' : null,
              ]
                .filter(Boolean)
                .join(' ') || undefined
            }
          />
          {urlError && (
            <p id="live-review-url-error" className="text-xs text-red-600 mt-1" role="alert">{urlError}</p>
          )}
          {figmaDesignLink && (
            <p id="live-review-figma-link-error" className="text-xs text-red-600 mt-1" role="alert">
              {FIGMA_DESIGN_LINK_REVIEW_ERROR}
            </p>
          )}
          {/* Our own dashboard, API root or review page: not a page to review,
              and the extension hint would be the wrong remedy (incident
              2026-09-10, bonkers.ie). Same copy the server answers with. */}
          {localKind === 'own-app' && (
            <p id="live-review-own-app-error" className="text-xs text-red-600 mt-1" role="alert">
              {URL_OWN_APP_REVIEW_ERROR}
            </p>
          )}
          {showReusedHint && (
            <p className="field-hint mt-2">
              Using your project&apos;s URL, edit to review a different page.
            </p>
          )}
          {localKind && localKind !== 'own-app' && <ExtensionCaptureHint reason={localKind} />}

          {showInstallCallout && (
            <div
              data-testid="extension-install-callout"
              className="mt-4"
            >
              <p className="field-hint mb-2">
                The extension lets you capture pages our proxy can't reach: localhost, login-gated pages, and sites that block automated requests.
              </p>
              <a
                href={CWS_INSTALL_URL}
                target="_blank"
                rel="noopener"
                className="inline-block px-3 py-1 rounded-full bg-primary-50 text-primary-700 font-medium text-xs hover:text-primary-800"
              ><ModalAction kind="export">
                Install the OverlayQA extension
              </ModalAction></a>
            </div>
          )}

          {isPending && (
            <p className="field-hint mt-4">
              Setting up your review. This can take a minute on slower sites.
              Closing this window won&apos;t cancel&nbsp;it.
            </p>
          )}

          <div className="flex justify-end gap-3 mt-5">
            <button
              type="button"
              onClick={handleUserClose}
              className="btn-secondary text-sm"
            ><ModalAction kind="cancel">
              Cancel
            </ModalAction></button>
            <button
              type="submit"
              className="btn-primary text-sm"
              disabled={isPending || !pageUrl.trim() || localKind !== null || figmaDesignLink}
            ><ModalAction kind="capture">
              {isPending ? 'Opening...' : 'Review'}
            </ModalAction></button>
          </div>
        </form>
      </div>
    </div>
  );

  return <ModalPortal>{overlay2}</ModalPortal>;
}
